import { EarlyAccessFeatures } from 'app/core/identity/user-identity';

export type TopLevelNavId =
  | 'dashboard'
  | 'onsite-experiences'
  | 'app-experiences'
  | 'web-analytics'
  | 'audiences'
  | 'automations'
  | 'assets'
  | 'community'
  | 'my-triggerbee'
  | 'work-in-progress'
  | 'backoffice-customers'
  | 'backoffice-accounts'
  | 'backoffice-users'
  | 'backoffice-customer-health-score'
  | 'backoffice-agent-conversations'
  | 'backoffice-activity-log'
  | 'backoffice-campaigns'
  | 'backoffice-tracker'
  | 'backoffice-media-collections'
  | 'backoffice-stripe-coupons'
  | 'backoffice-email-templates'
  | 'backoffice-campaign-classification'
  | 'backoffice-community';

export type FeatureFlagKey = 'appExperiences';

export type FeatureFlagState = Readonly<Record<FeatureFlagKey, boolean>>;

// Actions are non-route menu items handled imperatively by the host component
// (e.g. opening a dialog or signing out) rather than via routerLink.
export type SubNavAction = 'sign-out' | 'open-bugreport' | 'open-admin-settings' | 'enter-backoffice';

export interface SubNavItem {
  readonly type?: 'link';
  readonly translationKey: string;
  readonly url?: string;
  readonly queryParams?: Readonly<Record<string, string>>;
  readonly externalUrl?: string;
  readonly action?: SubNavAction;
  readonly dataTest?: string;
  readonly disabled?: boolean;
  readonly adminOnly?: boolean;
  // Rendered but disabled unless the current user is a site admin (mirrors the mega menu).
  readonly siteAdminOnly?: boolean;
  readonly betaTag?: boolean;
  readonly newTag?: boolean;
  readonly icon?: string;
  readonly upgradeTagAccessKey?:
    | 'isReferralsAllowed'
    | 'isProfilesAllowed'
    | 'isVisitsAllowed'
    | 'isSalesAnalyticsAllowed'
    | 'isInterestProfilesAllowed'
    | 'isActivityLogAllowed'
    | 'isAutomationsAllowed'
    | 'isCouponsAllowed';
  readonly productionHidden?: boolean;
  readonly children?: readonly SubNavItem[];
  // When set, the item is only shown to users who have this early-access feature enabled.
  readonly earlyAccessFeature?: EarlyAccessFeatures;
  readonly adminToggleOnly?: boolean;
}

export interface SubNavSeparator {
  readonly type: 'separator';
}

export interface SubNavGroup {
  readonly type: 'group';
  readonly translationKey: string;
  readonly dataTest?: string;
  readonly defaultExpanded?: boolean;
  readonly items: readonly SubNavItem[];
  readonly adminOnly?: boolean;
  // Whole group is hidden unless the current user is a real admin (identity.isAdmin()).
  readonly requiresAdmin?: boolean;
}

export type SubNavEntry = SubNavItem | SubNavSeparator | SubNavGroup;

export interface TopLevelNavItem {
  readonly id: TopLevelNavId;
  readonly translationKey: string;
  readonly icon: string;
  readonly url: string;
  readonly urlPrefixes: readonly string[];
  readonly dataTest: string;
  readonly subItems: readonly SubNavEntry[];
  readonly hidden?: boolean;
  // Draws a NEW label in the expanded rail and a dot that clears once any route under this url is opened.
  readonly newTag?: boolean;
  readonly adminOnly?: boolean;
  // Shows the admin marker icon without coupling visibility to the Admin Toggle
  // (use when the entry is gated by a featureFlag but is still an admin-only feature).
  readonly adminTag?: boolean;
  readonly backofficeOnly?: boolean;
  readonly featureFlag?: FeatureFlagKey;
  // Only rendered in the mobile navigation; excluded from the desktop top-level rail.
  readonly mobileOnly?: boolean;
  readonly upgradeTagAccessKey?: SubNavItem['upgradeTagAccessKey'];
  // No landing page of its own (e.g. "My Triggerbee"): mobile nav only toggles, never navigates.
  readonly mobileToggleOnly?: boolean;
}

export const TOP_LEVEL_NAV: readonly TopLevelNavItem[] = [
  {
    id: 'dashboard',
    translationKey: 'top-level-side-menu.dashboard',
    icon: 'space_dashboard',
    url: '/',
    urlPrefixes: ['/'],
    dataTest: 'top-level-dashboard',
    // The dashboard is a single page, so the rail navigates straight to it with no left nav.
    subItems: [],
  },
  {
    id: 'onsite-experiences',
    translationKey: 'top-level-side-menu.onsite-experiences',
    icon: 'layers',
    url: '/onsite-experiences',
    urlPrefixes: ['/onsite-experiences', '/campaigns', '/campaign-old'],
    dataTest: 'top-level-onsite-experiences',
    subItems: [],
  },
  {
    id: 'app-experiences',
    translationKey: 'top-level-side-menu.app-experiences',
    icon: 'smartphone',
    url: '/app-experiences',
    urlPrefixes: ['/app-experiences'],
    dataTest: 'top-level-app-experiences',
    featureFlag: 'appExperiences',
    subItems: [],
  },
  {
    id: 'web-analytics',
    translationKey: 'top-level-side-menu.web-analytics',
    icon: 'bar_chart',
    url: '/analytics',
    urlPrefixes: ['/analytics'],
    dataTest: 'top-level-web-analytics',
    // Empty so the side menu stays closed.
    subItems: [],
  },
  {
    id: 'audiences',
    translationKey: 'top-level-side-menu.audiences',
    icon: 'people',
    url: '/audiences',
    // The unlinked -old pages still highlight this section.
    urlPrefixes: [
      '/audiences',
      '/profiles',
      '/insights/visits',
      '/audiences-old',
      '/profiles-old',
      '/insights/visits-old',
    ],
    dataTest: 'menu-audiences',
    subItems: [
      {
        translationKey: 'people.title',
        url: '/profiles',
        dataTest: 'side-menu-audiences-profiles',
        upgradeTagAccessKey: 'isProfilesAllowed',
        children: [
          {
            translationKey: 'people.contact-fields.title',
            url: '/profiles/contact-fields',
            dataTest: 'side-menu-audiences-profiles-contact',
            siteAdminOnly: true,
          },
          {
            translationKey: 'data-platform.delete-profiles',
            url: '/profiles/delete',
            dataTest: 'side-menu-audiences-profiles-delete',
            siteAdminOnly: true,
          },
        ],
      },
      {
        translationKey: 'data-platform.visits',
        url: '/insights/visits',
        dataTest: 'side-menu-audiences-visits',
        upgradeTagAccessKey: 'isVisitsAllowed',
      },
    ],
  },
  {
    id: 'automations',
    translationKey: 'top-level-side-menu.automations',
    icon: 'bolt',
    url: '/automations',
    urlPrefixes: ['/automations'],
    dataTest: 'menu-automations',
    upgradeTagAccessKey: 'isAutomationsAllowed',
    subItems: [],
  },
  {
    id: 'assets',
    translationKey: 'top-level-side-menu.resources',
    icon: 'inventory_2',
    url: '/assets/medialibrary',
    urlPrefixes: [
      '/assets',
      '/data-collection',
      '/response-lists',
      '/campaigns/surveys/nps',
      '/campaigns/surveys/csat',
    ],
    dataTest: 'top-level-resources',
    subItems: [
      {
        translationKey: 'side-menu.resources.media',
        url: '/assets/medialibrary',
        dataTest: 'side-menu-resources-media',
      },
      {
        translationKey: 'settings.customization.brand-title',
        url: '/assets/brand-settings',
        dataTest: 'side-menu-resources-brand-settings',
      },
      {
        translationKey: 'side-menu.resources.coupons',
        url: '/assets/coupons',
        dataTest: 'side-menu-resources-coupons',
      },
      {
        translationKey: 'side-menu.resources.data-sources',
        url: '/assets/datasources',
        dataTest: 'side-menu-resources-data-sources',
        newTag: true,
      },
      {
        translationKey: 'side-menu.form-responses',
        url: '/response-lists',
        dataTest: 'side-menu-resources-form-responses',
        children: [
          {
            translationKey: 'nps.title',
            url: '/campaigns/surveys/nps',
            dataTest: 'side-menu-resources-form-responses-nps',
          },
          {
            translationKey: 'csat.title',
            url: '/campaigns/surveys/csat',
            dataTest: 'side-menu-resources-form-responses-csat',
          },
        ],
      },
      {
        translationKey: 'data-collection.nav-title',
        url: '/data-collection',
        dataTest: 'side-menu-resources-data-collection',
        newTag: true,
      },
    ],
  },
  {
    id: 'work-in-progress',
    translationKey: 'top-level-side-menu.work-in-progress',
    icon: 'construction',
    url: '/data-platform/funnels',
    urlPrefixes: ['/data-platform', '/normalized-attributes', '/content-affinity'],
    dataTest: 'top-level-work-in-progress',
    adminOnly: true,
    subItems: [
      {
        type: 'group',
        translationKey: 'data-platform.title',
        dataTest: 'side-menu-wip-data-platform',
        adminOnly: true,
        items: [
          {
            translationKey: 'data-platform.funnels',
            url: '/data-platform/funnels',
            dataTest: 'side-menu-data-platform-funnels',
          },
          {
            translationKey: 'data-platform.insights',
            url: '/data-platform/insights',
            dataTest: 'side-menu-data-platform-insights',
          },
          {
            translationKey: 'new-primary-dashboard.content-affinity.title',
            url: '/content-affinity',
            dataTest: 'side-menu-wip-content-affinity',
          },
        ],
      },
    ],
  },
  {
    id: 'community',
    translationKey: 'top-level-side-menu.community',
    icon: 'forum',
    url: '/knowledge',
    urlPrefixes: ['/knowledge'],
    dataTest: 'top-level-community',
    newTag: true,
    subItems: [
      {
        translationKey: 'side-menu.community.documentation',
        url: '/knowledge',
        dataTest: 'side-menu-community-documentation',
      },
      {
        translationKey: 'side-menu.community.release-notes',
        url: '/knowledge/release-notes',
        dataTest: 'side-menu-community-release-notes',
        newTag: true,
      },
      {
        translationKey: 'side-menu.community.discussion',
        url: '/knowledge/discussions',
        dataTest: 'side-menu-community-discussion',
      },
      {
        translationKey: 'side-menu.community.notifications',
        url: '/knowledge/notifications',
        dataTest: 'side-menu-community-notifications',
      },
      {
        translationKey: 'side-menu.community.request-feature',
        url: '/knowledge/feature-requests',
        dataTest: 'side-menu-community-request-feature',
      },
    ],
  },
  {
    // Mobile-only mirror of the desktop "My Triggerbee" mega menu (the gear icon). On desktop the
    // mega menu lives in the side menu; on mobile it surfaces here as a top-level accordion section.
    id: 'my-triggerbee',
    translationKey: 'settings.account.navigation.my-triggerbee',
    icon: 'settings',
    url: '/settings/general',
    urlPrefixes: ['/settings'],
    dataTest: 'top-level-my-triggerbee',
    mobileOnly: true,
    mobileToggleOnly: true,
    subItems: [
      {
        type: 'group',
        translationKey: 'settings.account.new-title',
        dataTest: 'side-menu-my-triggerbee-settings',
        items: [
          {
            translationKey: 'settings.account.navigation.general',
            url: '/settings/general',
            dataTest: 'side-menu-my-triggerbee-general',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.account.navigation.customization',
            url: '/settings/customization',
            dataTest: 'side-menu-my-triggerbee-customization',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.account.navigation.brand-settings',
            url: '/settings/customization',
            dataTest: 'side-menu-my-triggerbee-brand-settings',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.dimensions.title',
            url: '/settings/content-popularity',
            dataTest: 'side-menu-my-triggerbee-content-popularity',
          },
          {
            translationKey: 'settings.event.title',
            url: '/settings/events',
            dataTest: 'side-menu-my-triggerbee-events',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.account.navigation.onsite-campaign',
            url: '/settings/placeholders',
            dataTest: 'side-menu-my-triggerbee-onsite-campaign',
            siteAdminOnly: true,
          },
          {
            translationKey: 'consent.welcome.title',
            url: '/settings/policy',
            dataTest: 'side-menu-my-triggerbee-policy',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.account.navigation.profile-properties',
            url: '/settings/contact-fields',
            dataTest: 'side-menu-my-triggerbee-contact-fields',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.account.revenue.title',
            url: '/settings/revenue',
            dataTest: 'side-menu-my-triggerbee-revenue',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.account.navigation.profile',
            url: '/settings/profile',
            dataTest: 'side-menu-my-triggerbee-profile',
          },
          {
            translationKey: 'settings.account.navigation.users',
            url: '/settings/users',
            dataTest: 'side-menu-my-triggerbee-users',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.account.navigation.activity-log',
            url: '/settings/activity-log',
            dataTest: 'side-menu-my-triggerbee-activity-log',
            siteAdminOnly: true,
            upgradeTagAccessKey: 'isActivityLogAllowed',
          },
          {
            translationKey: 'settings.account.navigation.apps-integration',
            url: '/settings/apps',
            dataTest: 'side-menu-my-triggerbee-apps',
            siteAdminOnly: true,
          },
          {
            translationKey: 'settings.account.navigation.upgrade',
            url: '/settings/plan-billing',
            dataTest: 'side-menu-my-triggerbee-plan-billing',
            siteAdminOnly: true,
          },
        ],
      },
      {
        type: 'group',
        translationKey: 'settings.account.navigation.learn-help',
        dataTest: 'side-menu-my-triggerbee-learn-help',
        items: [
          {
            translationKey: 'settings.account.navigation.find-help',
            externalUrl: 'https://help.triggerbee.com/',
            dataTest: 'side-menu-my-triggerbee-find-help',
          },
          {
            translationKey: 'settings.account.navigation.news',
            url: '/knowledge/release-notes',
            dataTest: 'side-menu-my-triggerbee-news',
          },
          {
            translationKey: 'settings.account.navigation.request-feature',
            url: '/knowledge/feature-requests',
            dataTest: 'side-menu-my-triggerbee-request-feature',
          },
        ],
      },
      {
        type: 'group',
        translationKey: 'settings.account.navigation.admin-only',
        dataTest: 'side-menu-my-triggerbee-admin',
        adminOnly: true,
        requiresAdmin: true,
        items: [
          {
            translationKey: 'campaign.templates-title',
            url: '/backoffice/campaigns/templates',
            dataTest: 'side-menu-my-triggerbee-admin-templates',
          },
          {
            translationKey: 'email-templates.title',
            url: '/backoffice/email-templates',
            dataTest: 'side-menu-my-triggerbee-admin-email-templates',
          },
          {
            translationKey: 'campaign.filter-all',
            url: '/backoffice/campaigns/filter-all',
            dataTest: 'side-menu-my-triggerbee-admin-filter-all',
          },
          {
            translationKey: 'account-admin.title.bugreports',
            action: 'open-bugreport',
            dataTest: 'side-menu-my-triggerbee-admin-bugreports',
          },
          {
            translationKey: 'account-admin.title.quick-settings',
            action: 'open-admin-settings',
            dataTest: 'side-menu-my-triggerbee-admin-quick-settings',
          },
          {
            translationKey: 'account-admin.title.status-page',
            url: '/backoffice/status-page',
            dataTest: 'side-menu-my-triggerbee-admin-status-page',
          },
          {
            translationKey: 'account-admin.title.backoffice',
            action: 'enter-backoffice',
            dataTest: 'side-menu-my-triggerbee-admin-backoffice',
          },
        ],
      },
      { type: 'separator' },
      {
        translationKey: 'settings.account.navigation.sign-out',
        action: 'sign-out',
        dataTest: 'side-menu-my-triggerbee-sign-out',
      },
    ],
  },
  {
    id: 'backoffice-customers',
    translationKey: 'account-admin.title.customers',
    icon: 'folder_open',
    url: '/backoffice/customers',
    urlPrefixes: ['/backoffice/customers'],
    dataTest: 'top-level-backoffice-customers',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-accounts',
    translationKey: 'account-admin.title.accounts',
    icon: 'chrome_reader_mode',
    url: '/backoffice/accounts',
    urlPrefixes: ['/backoffice/accounts'],
    dataTest: 'top-level-backoffice-accounts',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-users',
    translationKey: 'account-admin.title.users',
    icon: 'person',
    url: '/backoffice/users',
    urlPrefixes: ['/backoffice/users'],
    dataTest: 'top-level-backoffice-users',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-customer-health-score',
    translationKey: 'account-admin.title.customer-health-score',
    icon: 'monitor_heart',
    url: '/backoffice/customer-health-score',
    urlPrefixes: ['/backoffice/customer-health-score'],
    dataTest: 'top-level-backoffice-customer-health-score',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-agent-conversations',
    translationKey: 'account-admin.title.agent-conversations',
    icon: 'forum',
    url: '/backoffice/agent-conversations',
    urlPrefixes: ['/backoffice/agent-conversations'],
    dataTest: 'top-level-backoffice-agent-conversations',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-activity-log',
    translationKey: 'account-admin.title.activity-log',
    icon: 'history_toggle_off',
    url: '/backoffice/activity-log',
    urlPrefixes: ['/backoffice/activity-log'],
    dataTest: 'top-level-backoffice-activity-log',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-campaigns',
    translationKey: 'account-admin.title.campaigns.title',
    icon: 'settings',
    url: '/backoffice/campaigns/templates',
    urlPrefixes: ['/backoffice/campaigns'],
    dataTest: 'top-level-backoffice-campaigns',
    backofficeOnly: true,
    subItems: [
      {
        translationKey: 'account-admin.title.campaigns.templates',
        url: '/backoffice/campaigns/templates',
        dataTest: 'side-menu-backoffice-campaigns-templates',
      },
      {
        translationKey: 'account-admin.title.campaigns.filter-all-campaigns',
        url: '/backoffice/campaigns/filter-all',
        dataTest: 'side-menu-backoffice-campaigns-filter-all',
      },
      {
        translationKey: 'account-admin.title.campaigns.export-widgets',
        url: '/backoffice/campaigns/export',
        dataTest: 'side-menu-backoffice-campaigns-export',
      },
      {
        translationKey: 'Developer',
        url: '/backoffice/campaigns/developer',
        dataTest: 'side-menu-backoffice-campaigns-developer',
        productionHidden: true,
      },
    ],
  },
  {
    id: 'backoffice-tracker',
    translationKey: 'account-admin.title.tracker',
    icon: 'sensors',
    url: '/backoffice/tracker',
    urlPrefixes: ['/backoffice/tracker'],
    dataTest: 'top-level-backoffice-tracker',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-media-collections',
    translationKey: 'account-admin.title.media-collections',
    icon: 'image',
    url: '/backoffice/media-collections',
    urlPrefixes: ['/backoffice/media-collections'],
    dataTest: 'top-level-backoffice-media-collections',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-stripe-coupons',
    translationKey: 'account-admin.title.stripe-coupons',
    icon: 'sell',
    url: '/backoffice/stripe-coupons',
    urlPrefixes: ['/backoffice/stripe-coupons'],
    dataTest: 'top-level-backoffice-stripe-coupons',
    backofficeOnly: true,
    subItems: [],
  },
  {
    // The release-notes authoring surface. It lives here rather than under /knowledge because every
    // route it calls is community/admin/*, which stays UserRole.Admin after the reader opens up.
    id: 'backoffice-community',
    translationKey: 'account-admin.title.community',
    icon: 'forum',
    url: '/backoffice/community',
    urlPrefixes: ['/backoffice/community'],
    dataTest: 'top-level-backoffice-community',
    backofficeOnly: true,
    subItems: [
      {
        translationKey: 'knowledge.adminstudio.title',
        url: '/backoffice/community/review',
        dataTest: 'side-menu-backoffice-community-adminstudio',
      },
      {
        translationKey: 'knowledge.help-centre-admin.title',
        url: '/backoffice/community/help-centre',
        dataTest: 'side-menu-backoffice-community-help-centre',
      },
    ],
  },
  {
    id: 'backoffice-email-templates',
    translationKey: 'email-templates.title',
    icon: 'mail_outline',
    url: '/backoffice/email-templates',
    urlPrefixes: ['/backoffice/email-templates'],
    dataTest: 'top-level-backoffice-email-templates',
    backofficeOnly: true,
    subItems: [],
  },
  {
    id: 'backoffice-campaign-classification',
    translationKey: 'classification.navTitle',
    icon: 'label',
    url: '/backoffice/campaign-classification',
    urlPrefixes: ['/backoffice/campaign-classification'],
    dataTest: 'top-level-backoffice-campaign-classification',
    backofficeOnly: true,
    subItems: [
      {
        translationKey: 'classification.search.title',
        url: '/backoffice/campaign-classification',
        dataTest: 'side-menu-backoffice-classification-search',
      },
      {
        translationKey: 'classification.review.title',
        url: '/backoffice/campaign-classification/review',
        dataTest: 'side-menu-backoffice-classification-review',
      },
      {
        translationKey: 'classification.stats.title',
        url: '/backoffice/campaign-classification/review/stats',
        dataTest: 'side-menu-backoffice-classification-stats',
      },
      {
        translationKey: 'classification.global.title',
        url: '/backoffice/campaign-classification/global-job',
        dataTest: 'side-menu-backoffice-classification-global',
      },
      {
        translationKey: 'classification.rules.title',
        url: '/backoffice/campaign-classification/rules-file',
        dataTest: 'side-menu-backoffice-classification-rules-file',
      },
    ],
  },
];

export function findActiveTopLevel(url: string): TopLevelNavItem | undefined {
  const [rawPath] = url.split('?');
  const path = rawPath || '/';
  // Mobile-only sections (e.g. "My Triggerbee") never drive the desktop side menu's active state,
  // so they are excluded here; their routes belong to other layouts (e.g. the settings shell).
  const matches = TOP_LEVEL_NAV.filter(
    (item) =>
      !item.mobileOnly &&
      item.urlPrefixes.some((prefix) =>
        prefix === '/' ? path === '/' : path === prefix || path.startsWith(prefix + '/'),
      ),
  );
  if (matches.length === 0) {
    return undefined;
  }
  const matchingLength = (item: TopLevelNavItem): number =>
    Math.max(
      ...item.urlPrefixes
        .filter((prefix) => (prefix === '/' ? path === '/' : path === prefix || path.startsWith(prefix + '/')))
        .map((p) => p.length),
    );
  return matches.reduce((best, current) => (matchingLength(current) > matchingLength(best) ? current : best));
}

export interface CreateTarget {
  readonly url: string;
  readonly queryParams: Record<string, string> | null;
}

// Creating from a type's own list page skips the type step of the template picker.
// Null hides the button: the backoffice has no campaigns of its own.
export function resolveCreateTarget(path: string): CreateTarget | null {
  if (path.startsWith('/backoffice')) {
    return null;
  }
  if (path.startsWith('/campaigns/promotions')) {
    return { url: '/campaigns/create/templates', queryParams: { type: 'promotion' } };
  }
  if (path.startsWith('/campaigns/forms')) {
    return { url: '/campaigns/create/templates', queryParams: { type: 'form' } };
  }
  if (path.startsWith('/campaigns/surveys')) {
    return { url: '/campaigns/create/templates', queryParams: { type: 'survey' } };
  }
  if (path.startsWith('/campaigns/ab-tests')) {
    return { url: '/campaigns/ab-tests/create/templates', queryParams: null };
  }
  return { url: '/campaigns/create/templates', queryParams: null };
}

export function isSubNavSeparator(entry: SubNavEntry): entry is SubNavSeparator {
  return (entry as { type?: string }).type === 'separator';
}

export function isSubNavGroup(entry: SubNavEntry): entry is SubNavGroup {
  return (entry as { type?: string }).type === 'group';
}

function isUrlActive(url: string | undefined, path: string): boolean {
  return !!url && (path === url || path.startsWith(url + '/'));
}

export function isSubNavItemExpanded(item: SubNavItem, path: string): boolean {
  return isUrlActive(item.url, path) || (item.children ?? []).some((child) => isUrlActive(child.url, path));
}

function filterProductionHidden(items: readonly SubNavEntry[]): readonly SubNavEntry[] {
  const result: SubNavEntry[] = [];
  for (const entry of items) {
    if (isSubNavSeparator(entry) || isSubNavGroup(entry)) {
      result.push(entry);
      continue;
    }
    if (entry.productionHidden) continue;
    result.push(entry);
  }
  return result;
}

// Drops items gated behind an early-access feature the current user has not enabled.
// Separators and groups are never early-access gated, so they always pass through.
function filterEarlyAccess(
  items: readonly SubNavEntry[],
  granted: readonly EarlyAccessFeatures[],
): readonly SubNavEntry[] {
  const result: SubNavEntry[] = [];
  for (const entry of items) {
    if (isSubNavSeparator(entry) || isSubNavGroup(entry)) {
      result.push(entry);
      continue;
    }
    if (entry.earlyAccessFeature && !granted.includes(entry.earlyAccessFeature)) {
      continue;
    }
    result.push(entry);
  }
  return result;
}

function filterAdminSettings(items: readonly SubNavEntry[], adminToggle: boolean): readonly SubNavEntry[] {
  const result: SubNavEntry[] = [];
  for (const entry of items) {
    if (isSubNavSeparator(entry) || isSubNavGroup(entry)) {
      result.push(entry);
      continue;
    }
    if (entry.adminToggleOnly && !adminToggle) {
      continue;
    }
    result.push(entry);
  }
  return result;
}

// Single source of truth for resolving the visible sub-navigation entries of a top-level
// item, shared by the desktop side-menu and the mobile accordion so they never drift.
export function resolveSubNavEntries(
  topLevel: TopLevelNavItem | undefined,
  options: {
    isProduction: boolean;
    earlyAccessFeatures: readonly EarlyAccessFeatures[];
    adminToggle: boolean;
  },
): readonly SubNavEntry[] {
  const items = topLevel?.subItems ?? [];
  const productionFiltered = options.isProduction ? filterProductionHidden(items) : items;
  const earlyAccessFiltered = filterEarlyAccess(productionFiltered, options.earlyAccessFeatures);
  return filterAdminSettings(earlyAccessFiltered, options.adminToggle);
}
